import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatAppDate } from '../../services/appConfigRuntime';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  AlertTitle,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Folder,
  FolderOpen,
  Description,
  Image,
  PictureAsPdf,
  Architecture,
  Engineering,
  Share,
  Visibility,
  MoreVert,
  ArrowBack,
  CloudUpload,
  CheckCircle,
  Error,
  HourglassEmpty,
  Refresh,
  History as HistoryIcon,
  Update as UpdateIcon,
} from '@mui/icons-material';
import { filesApi, storageApi, foldersApi, forgeApi, usageApi } from '../../services/fileShareApi';
import { translationStartErrorMessage } from './fileShareTranslationErrors';
import TranslationHistoryDialog from './TranslationHistoryDialog';
import { toast } from 'react-toastify';

const FolderContentView = ({ 
  projectId, 
  currentFolderId, 
  currentFolderPath = [],
  onFolderClick,
  onFileSelect,
  selectedFile,
  refreshTrigger,
  onShare,
  onBack
}) => {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFileForMenu, setSelectedFileForMenu] = useState(null);
  const [translatingFiles, setTranslatingFiles] = useState(new Set()); // Track files being translated
  const [translationUsage, setTranslationUsage] = useState(null);
  const [overageDialog, setOverageDialog] = useState({
    open: false,
    file: null,
    warnings: [],
    confirmationWord: 'APPROVE',
    typedValue: '',
    forceRetranslate: false,
  });
  const [retranslateDialog, setRetranslateDialog] = useState({
    open: false,
    file: null,
  });
  const [historyDialog, setHistoryDialog] = useState({
    open: false,
    file: null,
  });

  // Max 3 translations at a time — block starting another when already at capacity
  const MAX_CONCURRENT_TRANSLATIONS = 3;

  const inProgressTranslationCount = useMemo(() => {
    const ids = new Set();
    translatingFiles.forEach((id) => {
      if (id != null) ids.add(String(id));
    });
    (files || []).forEach((f) => {
      const status = f.forgeStatus || f.translationStatus;
      if (status === 'IN_PROGRESS' || status === 'INPROGRESS') {
        const id = f.fileId || f.id;
        if (id != null) ids.add(String(id));
      }
    });
    return ids.size;
  }, [files, translatingFiles]);

  const isTranslationBusyFor = useCallback(
    (fileId) => {
      if (fileId == null) return false;
      const id = String(fileId);
      const thisInProgress =
        translatingFiles.has(fileId) ||
        translatingFiles.has(id) ||
        (files || []).some((f) => {
          const fid = String(f.fileId || f.id);
          const status = f.forgeStatus || f.translationStatus;
          return fid === id && (status === 'IN_PROGRESS' || status === 'INPROGRESS');
        });
      if (thisInProgress) return false;
      return inProgressTranslationCount >= MAX_CONCURRENT_TRANSLATIONS;
    },
    [files, translatingFiles, inProgressTranslationCount]
  );

  const fetchTranslationUsage = useCallback(async () => {
    try {
      const response = await usageApi.getCurrent();
      setTranslationUsage(response.data || null);
    } catch (err) {
      // Usage information should not prevent browsing files.
      console.warn('Could not load File Share translation usage:', err.message);
      setTranslationUsage(null);
    }
  }, []);

  const fetchContent = useCallback(async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch folders for current location
      let filteredFolders = [];
      if (currentFolderId) {
        // Get direct subfolders of current folder
        console.log('🔍 FolderContentView: Fetching subfolders for folderId:', currentFolderId);
        const subfoldersResponse = await foldersApi.getSubFolders(currentFolderId);
        console.log('🔍 FolderContentView: Subfolders response:', subfoldersResponse.data);
        filteredFolders = (subfoldersResponse.data || []).map(f => ({
          ...f,
          folderId: f.folderId || f.id,
          folderName: f.folderName || f.name,
          parentFolderId: f.parentFolderId,
          type: 'folder',
        }));
        console.log('🔍 FolderContentView: Mapped folders:', filteredFolders);
      } else {
        // Get root folders (no parent)
        console.log('🔍 FolderContentView: Fetching root folders for projectId:', projectId);
        const rootResponse = await foldersApi.getRootByProject(projectId);
        console.log('🔍 FolderContentView: Root folders response:', rootResponse.data);
        filteredFolders = (rootResponse.data || []).map(f => ({
          ...f,
          folderId: f.folderId || f.id,
          folderName: f.folderName || f.name,
          parentFolderId: f.parentFolderId,
          type: 'folder',
        }));
        console.log('🔍 FolderContentView: Mapped root folders:', filteredFolders);
      }

      setFolders(filteredFolders);

      // Fetch files for current folder
      let fileResponse;
      if (currentFolderId) {
        fileResponse = await storageApi.getUnifiedFolderFiles(projectId, currentFolderId, '');
      } else {
        fileResponse = await storageApi.getUnifiedProjectFiles(projectId, '');
      }

      if (fileResponse?.data?.success) {
        let allFiles = fileResponse.data.files || [];

        // Project root: do not show files that belong inside a root-level folder.
        // Those files appear when that folder is opened (Explorer / Drive style).
        if (!currentFolderId && filteredFolders.length > 0) {
          const rootFolderIds = new Set(
            filteredFolders.map((f) => String(f.folderId || f.id)).filter(Boolean)
          );
          allFiles = allFiles.filter((file) => {
            const fileFolderId = file.folderId != null ? String(file.folderId) : null;
            if (!fileFolderId) return true; // storage-only / unassigned root files
            return !rootFolderIds.has(fileFolderId);
          });
        }

        setFiles(allFiles);
        // Soft warning (e.g. tenant context) — keep content visible
        if (fileResponse.data.warning) {
          setError(fileResponse.data.warning);
        }
      } else {
        setFiles([]);
        const storageMsg =
          fileResponse?.data?.message ||
          fileResponse?.data?.error ||
          'Failed to load files from storage';
        setError(storageMsg);
      }
    } catch (err) {
      const storageMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to load content';
      setError(storageMsg);
      console.error('Error fetching content:', err);
      setFolders([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, currentFolderId, refreshTrigger]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  useEffect(() => {
    fetchTranslationUsage();
  }, [fetchTranslationUsage]);

  // Poll for progress updates on files that are uploading or translating
  useEffect(() => {
    const isRealFileId = (id) => {
      if (!id) return false;
      const s = String(id);
      if (s.startsWith('storage_')) return false;
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
    };
    const filesInProgress = files.filter(file => {
      const fileId = file.fileId || file.id;
      const forgeStatus = file.forgeStatus || file.translationStatus;
      const fileType = file.fileType || file.type;
      // Only poll when we have a real DB fileId (UUID); translation-status API requires UUID
      if (!fileId || !isRealFileId(fileId)) return false;
      return (
        (forgeStatus === 'IN_PROGRESS' || forgeStatus === 'INPROGRESS') ||
        translatingFiles.has(fileId)
      ) &&
      isForgeSupported(fileType);
    });

    if (filesInProgress.length === 0) {
      return;
    }

    // Poll immediately, then every 2 seconds for faster updates
    const pollProgress = async () => {
      for (const file of filesInProgress) {
        try {
          const fileId = file.fileId || file.id;
          const response = await filesApi.getTranslationStatus(fileId);
          
          const newStatus = response.data.status;
          let uploadProgress = response.data.uploadProgress;
          let translationProgress = response.data.translationProgress;
          
          // Preserve last known progress when API returns null during IN_PROGRESS (avoids bar disappearing)
          setFiles(prevFiles =>
            prevFiles.map(f => {
              if ((f.fileId || f.id) !== fileId) return f;
              const inProgress = newStatus === 'IN_PROGRESS' || newStatus === 'INPROGRESS';
              const keepUpload = inProgress && (uploadProgress == null) && (f.uploadProgress != null);
              const keepTranslation = inProgress && (translationProgress == null) && (f.translationProgress != null);
              return {
                ...f,
                uploadProgress: keepUpload ? f.uploadProgress : uploadProgress,
                translationProgress: keepTranslation ? f.translationProgress : translationProgress,
                forgeStatus: newStatus
              };
            })
          );
          
          console.log('📊 FolderContentView: Progress update for file:', fileId, {
            uploadProgress: uploadProgress ?? (files.find(f => (f.fileId || f.id) === fileId)?.uploadProgress),
            translationProgress: translationProgress ?? (files.find(f => (f.fileId || f.id) === fileId)?.translationProgress),
            status: newStatus
          });
          
          // If status is confirmed as IN_PROGRESS, remove from translatingFiles
          // (it will be tracked by status now)
          if (newStatus === 'IN_PROGRESS' || newStatus === 'INPROGRESS') {
            setTranslatingFiles(prev => {
              const newSet = new Set(prev);
              newSet.delete(fileId);
              return newSet;
            });
          }
          
          // If status is SUCCESS or FAILED, also remove from translatingFiles
          if (newStatus === 'SUCCESS' || newStatus === 'COMPLETE' || newStatus === 'FAILED') {
            setTranslatingFiles(prev => {
              const newSet = new Set(prev);
              newSet.delete(fileId);
              return newSet;
            });
            if (newStatus === 'SUCCESS' || newStatus === 'COMPLETE') {
              fetchTranslationUsage();
            }
            if (newStatus === 'FAILED' && (file.forgeStatus === 'IN_PROGRESS' || file.forgeStatus === 'INPROGRESS')) {
              toast.error('Translation failed. You can retry this file.', {
                autoClose: 8000,
                toastId: `translate-failed-${fileId}`,
              });
            }
          }
        } catch (err) {
          const status = err.response?.status;
          const body = err.response?.data;
          console.error('Error polling progress for file:', file.fileId || file.id, status ? `HTTP ${status}` : err.message, body || err);
          // On error, remove from translatingFiles to prevent infinite polling
          const fileId = file.fileId || file.id;
          setTranslatingFiles(prev => {
            const newSet = new Set(prev);
            newSet.delete(fileId);
            return newSet;
          });
        }
      }
    };

    // Poll immediately
    pollProgress();
    
    // Then poll every 2 seconds for faster updates
    const pollInterval = setInterval(pollProgress, 2000);

    return () => clearInterval(pollInterval);
  }, [files, translatingFiles, fetchTranslationUsage]);

  const handleFolderClick = (folder) => {
    if (onFolderClick) {
      onFolderClick({
        folderId: folder.folderId || folder.id,
        folderName: folder.folderName || folder.name,
        parentFolderId: folder.parentFolderId,
      });
    }
  };

  const handleFileClick = (file) => {
    if (onFileSelect) {
      onFileSelect(file);
    }
  };

  const handleMenuOpen = (event, file) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedFileForMenu(file);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFileForMenu(null);
  };

  const getFileIcon = (fileType) => {
    switch (fileType?.toUpperCase()) {
      case 'RVT':
      case 'DWG':
        return <Architecture sx={{ fontSize: 56, color: 'primary.main' }} />;
      case 'PDF':
        return <PictureAsPdf sx={{ fontSize: 56, color: 'error.main' }} />;
      case 'IMAGE':
      case 'JPG':
      case 'JPEG':
      case 'PNG':
      case 'GIF':
        return <Image sx={{ fontSize: 56, color: 'success.main' }} />;
      default:
        return <Description sx={{ fontSize: 56, color: 'text.secondary' }} />;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return formatAppDate(dateString);
  };

  // Check if file type is Forge-supported
  const isForgeSupported = (fileType) => {
    if (!fileType) return false;
    const type = fileType.toUpperCase();
    return ['RVT', 'DWG', 'IFC', 'FBX', 'OBJ', '3DS', 'DAE', 'STL'].includes(type);
  };

  // Get translation status badge
  const getTranslationStatusBadge = (file) => {
    const fileId = file.fileId || file.id;
    const forgeStatus = file.forgeStatus || file.translationStatus;
    const fileType = file.fileType || file.type;
    
    if (!isForgeSupported(fileType)) {
      return null; // Not a Forge-supported file type
    }

    if (translatingFiles.has(fileId)) {
      return (
        <Chip
          icon={<HourglassEmpty sx={{ fontSize: 14 }} />}
          label="Translating..."
          size="small"
          color="info"
          sx={{ fontSize: '0.65rem', height: 20 }}
        />
      );
    }

    switch (forgeStatus) {
      case 'SUCCESS':
      case 'COMPLETE':
        if (file.contentOutdated) {
          return (
            <Chip
              icon={<UpdateIcon sx={{ fontSize: 14 }} />}
              label="Outdated"
              size="small"
              color="warning"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
          );
        }
        return (
          <Chip
            icon={<CheckCircle sx={{ fontSize: 14 }} />}
            label="Translated"
            size="small"
            color="success"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        );
      case 'IN_PROGRESS':
      case 'INPROGRESS':
        return (
          <Chip
            icon={<HourglassEmpty sx={{ fontSize: 14 }} />}
            label="Translating"
            size="small"
            color="info"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        );
      case 'FAILED':
        return (
          <Chip
            icon={<Error sx={{ fontSize: 14 }} />}
            label="Failed"
            size="small"
            color="error"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        );
      case 'PENDING':
      default:
        // Show translate button for PENDING or no status
        return null;
    }
  };

  // Handle translate / re-translate action
  const startTranslation = async (file, overageApproved = null, forceRetranslate = false) => {
    let fileId = file.fileId || file.id;

    try {
      setTranslatingFiles(prev => new Set(prev).add(fileId));

      console.log('🔄 Starting translation for file:', file.fileName || file.name, 'fileId:', fileId,
        forceRetranslate ? '(re-translate)' : '');

      const response = await forgeApi.uploadToForge(fileId, {
        ...(overageApproved ? { overageApproved } : {}),
        ...(forceRetranslate ? { forceRetranslate: true } : {}),
      });

      console.log('✅ Translation started:', response.data);

      const uploadProg = response.data.uploadProgress;
      const translationProg = response.data.translationProgress;
      const newStatus = response.data.status || 'IN_PROGRESS';

      setFiles(prevFiles =>
        prevFiles.map(f => {
          if ((f.fileId || f.id) === fileId) {
            return {
              ...f,
              uploadProgress: uploadProg !== undefined ? uploadProg : 0.0,
              translationProgress: translationProg,
              forgeStatus: newStatus
            };
          }
          return f;
        })
      );

      if (forceRetranslate) {
        toast.success('Re-translation started');
      }

      await fetchContent();
      await fetchTranslationUsage();
    } catch (error) {
      console.error('❌ Translation error:', error);
      const payload = error.response?.data || {};
      const errorMessage = translationStartErrorMessage(payload, error.message);

      if (payload.error === 'USAGE_CONFIRMATION_REQUIRED' || payload.requiresConfirmation) {
        setOverageDialog({
          open: true,
          file,
          warnings: Array.isArray(payload.warnings) ? payload.warnings : [errorMessage],
          confirmationWord: payload.confirmationWord || 'APPROVE',
          typedValue: '',
          forceRetranslate: !!forceRetranslate,
        });
        fetchTranslationUsage();
      } else if (payload.error === 'TRANSLATION_IN_PROGRESS') {
        toast.warning(
          payload.message ||
            'Maximum of 3 translations can run at the same time. Wait until one finishes, then try again.'
        );
      } else {
        toast.error(errorMessage, { autoClose: 10000, toastId: `translate-error-${fileId}-${Date.now()}` });
        setFiles(prevFiles =>
          prevFiles.map(f =>
            (f.fileId || f.id) === fileId ? { ...f, forgeStatus: 'FAILED' } : f
          )
        );
      }

      setTranslatingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  };

  const handleTranslate = async (file, forceRetranslate = false) => {
    let fileId = file.fileId || file.id;
    
    // Check if this is a storage file (id starts with "storage_")
    // For storage files, we need to check if they've been synced to database
    if (fileId && typeof fileId === 'string' && fileId.startsWith('storage_')) {
      toast.error('Please sync files from storage first. Files need to be in the database before translation.');
      handleMenuClose();
      return;
    }
    
    if (!fileId) {
      console.error('Cannot translate: file has no fileId', file);
      toast.error('Cannot translate file: file ID not found. Please sync files from storage first.');
      handleMenuClose();
      return;
    }

    if (isTranslationBusyFor(fileId)) {
      toast.warning(
        'Maximum of 3 translations can run at the same time. Wait until one completes or fails, then try again.'
      );
      handleMenuClose();
      return;
    }

    handleMenuClose();
    await startTranslation(file, null, forceRetranslate);
  };

  const requestRetranslate = (file) => {
    handleMenuClose();
    setRetranslateDialog({ open: true, file });
  };

  const closeRetranslateDialog = () => {
    setRetranslateDialog({ open: false, file: null });
  };

  const confirmRetranslate = async () => {
    const file = retranslateDialog.file;
    closeRetranslateDialog();
    if (file) {
      await handleTranslate(file, true);
    }
  };

  const closeOverageDialog = () => {
    setOverageDialog({
      open: false,
      file: null,
      warnings: [],
      confirmationWord: 'APPROVE',
      typedValue: '',
      forceRetranslate: false,
    });
  };

  const confirmOverageTranslate = async () => {
    const required = (overageDialog.confirmationWord || 'APPROVE').trim();
    if (overageDialog.typedValue.trim().toUpperCase() !== required.toUpperCase()) {
      toast.warning(`Type ${required.toLowerCase()} to continue.`);
      return;
    }
    const file = overageDialog.file;
    const forceRetranslate = !!overageDialog.forceRetranslate;
    closeOverageDialog();
    if (file) {
      await startTranslation(file, required.toUpperCase(), forceRetranslate);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isStorageAuthError =
    !!error &&
    /401|403|404|authentication|credential|forbidden|unauthorized|access token|path not found|failed to list|connector/i.test(
      error
    );

  const allItems = [
    ...folders.map(f => ({ ...f, itemType: 'folder' })),
    ...files.map(f => ({ 
      ...f, 
      itemType: 'file',
      // Preserve fileType from API response - API returns 'type' as fileType, so map it
      fileType: f.fileType || f.type || (f.name ? f.name.split('.').pop()?.toUpperCase() : null)
    }))
  ];

  // Hard storage failure with nothing to show — full error (not "empty folder")
  if (error && isStorageAuthError && allItems.length === 0) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <AlertTitle>Cannot access storage</AlertTitle>
        {error}
        <Typography variant="body2" sx={{ mt: 1 }}>
          Check the Egnyte connector credentials and the project root folder path, then use{' '}
          <strong>Test Connection</strong> or sync folders again.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Note: Breadcrumbs are shown in FileShare.jsx, not here to avoid duplication */}

      {error && (
        <Alert severity={isStorageAuthError ? 'error' : 'warning'} sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      {translationUsage && (
        <Alert severity="info" icon={false} sx={{ mb: 2, py: 0.5, flexShrink: 0 }}>
          <Typography variant="body2">
            Translation usage ({translationUsage.yearMonth}):{' '}
            <strong>{translationUsage.translationsUsed || 0}</strong>
            {translationUsage.translationsLimit != null
              ? ` / ${translationUsage.translationsLimit} files`
              : ' files'}
            {translationUsage.translationsOverage > 0 && (
              <>
                {' · '}
                <strong>
                  Overage: {translationUsage.translationsOverage || 0} files
                </strong>
              </>
            )}
          </Typography>
        </Alert>
      )}

      {/* Content Grid */}
      {allItems.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <Typography variant="h6" gutterBottom>
            This folder is empty
          </Typography>
          <Typography variant="body2">
            Upload files or create folders to get started
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ flex: 1, overflowY: 'auto', pb: 2 }}>
          {allItems.map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={item.folderId || item.fileId || item.id}>
              <Card
                sx={{
                  // minHeight keeps grid look; auto height prevents clipping progress/badge/button
                  minHeight: 220,
                  height: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'visible',
                  transition: 'box-shadow 0.2s, border-color 0.2s',
                  // Always reserve 2px border so select/hover never shifts content
                  border: 2,
                  borderColor:
                    selectedFile?.fileId === item.fileId ? 'primary.main' : 'transparent',
                  '&:hover': {
                    boxShadow: 4,
                  },
                  position: 'relative', // For absolute positioned menu button
                }}
              >
                  <CardActionArea
                  onClick={() => {
                    if (item.itemType === 'folder' || item.type === 'folder') {
                      console.log('🔍 FolderContentView: Clicked folder:', item);
                      handleFolderClick(item);
                    } else {
                      handleFileClick(item);
                    }
                  }}
                  sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'stretch',
                    height: 'auto',
                  }}
                >
                  <CardContent sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    p: 2,
                    height: 'auto',
                    overflow: 'visible',
                    '&:last-child': { pb: 2 } // Override Material-UI default padding
                  }}>
                    {/* Icon */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      mb: 1.5, 
                      height: 72,
                      alignItems: 'center',
                      flexShrink: 0,
                    }}>
                      {(item.itemType === 'folder' || item.type === 'folder') ? (
                        <FolderOpen sx={{ fontSize: 56, color: 'primary.main' }} />
                      ) : (
                        getFileIcon(item.fileType || item.type)
                      )}
                    </Box>

                    {/* Name */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        minHeight: 20,
                        flexShrink: 0,
                      }}
                      title={item.folderName || item.fileName || item.name}
                    >
                      {item.folderName || item.fileName || item.name}
                    </Typography>

                    {/* Metadata */}
                    <Box sx={{ 
                      mt: 'auto', 
                      pt: 1, 
                      borderTop: 1, 
                      borderColor: 'divider',
                      flexShrink: 0,
                    }}>
                      {(item.itemType === 'file' || (item.type !== 'folder' && item.itemType !== 'folder')) && (
                        <>
                          {/* Translation Status Badge */}
                          {getTranslationStatusBadge(item) && (
                            <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'center' }}>
                              {getTranslationStatusBadge(item)}
                            </Box>
                          )}
                          {/* Progress Indicators - show when in progress or when FAILED (show last % so user sees "Failed at 50%") */}
                          {(() => {
                            const fileId = item.fileId || item.id;
                            const forgeStatus = item.forgeStatus || item.translationStatus;
                            const isTranslating = translatingFiles.has(fileId);
                            const isActivelyTranslating = isTranslating ||
                                                          forgeStatus === 'IN_PROGRESS' ||
                                                          forgeStatus === 'INPROGRESS';
                            const isFailed = forgeStatus === 'FAILED';
                            // Show progress when actively translating OR when FAILED with partial progress (so user sees "Failed at 50%")
                            const showProgressSection = isActivelyTranslating || isFailed;

                            if (!showProgressSection) {
                              return null;
                            }

                            const showTranslationProgress = item.translationProgress !== null &&
                                                           item.translationProgress !== undefined &&
                                                           item.translationProgress < 100;
                            // URN / 100% means upload finished — don't label it "Failed at Upload"
                            // when only Autodesk translation failed (stale uploadProgress can be e.g. 10%).
                            const uploadSucceeded =
                              Boolean(item.forgeUrn) ||
                              (item.uploadProgress != null && item.uploadProgress >= 100);
                            const uploadFailed = isFailed && !uploadSucceeded;
                            const uploadComplete = uploadSucceeded;
                            const displayUploadPct = uploadSucceeded
                              ? Math.max(item.uploadProgress ?? 0, 100)
                              : item.uploadProgress;
                            const showUploadBar =
                              displayUploadPct != null &&
                              (displayUploadPct < 100 ||
                                (displayUploadPct >= 100 && showTranslationProgress) ||
                                (isFailed && uploadSucceeded));

                            if (!showUploadBar && !showTranslationProgress) {
                              return null;
                            }

                            return (
                              <Box sx={{ mt: 0.5, px: 1 }}>
                                {showUploadBar && (
                                  <Box sx={{ mb: 0.5 }}>
                                    <Typography
                                      variant="caption"
                                      color={
                                        uploadFailed
                                          ? 'error'
                                          : uploadComplete
                                          ? 'success.main'
                                          : 'text.secondary'
                                      }
                                      sx={{ fontSize: '0.65rem' }}
                                    >
                                      {uploadFailed
                                        ? `Failed at Upload: ${displayUploadPct.toFixed(0)}%`
                                        : uploadComplete
                                        ? `Upload: ${displayUploadPct.toFixed(0)}% ✓`
                                        : `Upload: ${displayUploadPct.toFixed(0)}%`}
                                    </Typography>
                                    <LinearProgress
                                      variant="determinate"
                                      value={Math.min(100, displayUploadPct)}
                                      color={
                                        uploadFailed
                                          ? 'error'
                                          : uploadComplete
                                          ? 'success'
                                          : 'primary'
                                      }
                                      sx={{ height: 4, borderRadius: 2, mt: 0.25 }}
                                    />
                                  </Box>
                                )}
                                {showTranslationProgress && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      color={isFailed && !uploadFailed ? 'error' : 'text.secondary'}
                                      sx={{ fontSize: '0.65rem' }}
                                    >
                                      {isFailed && !uploadFailed
                                        ? `Failed at Translation: ${item.translationProgress.toFixed(0)}%`
                                        : `Translation: ${item.translationProgress.toFixed(0)}%`}
                                    </Typography>
                                    <LinearProgress
                                      variant="determinate"
                                      value={item.translationProgress}
                                      color={isFailed && !uploadFailed ? 'error' : 'secondary'}
                                      sx={{ height: 4, borderRadius: 2, mt: 0.25 }}
                                    />
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                            {formatFileSize(item.size || item.fileSize)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                            {formatDate(item.createdAt || item.uploadDate)}
                          </Typography>
                          {item.uploadedBy && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ 
                              lineHeight: 1.4,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              By {item.uploadedBy}
                            </Typography>
                          )}
                          {/* Translate/Retry on card for pending/failed only. Re-translate is 3-dot menu only. */}
                          {(() => {
                            const fileName = item.name || item.fileName || '';
                            const fileType = item.fileType || item.type || item.originalType || 
                              (fileName.includes('.') ? fileName.split('.').pop().toUpperCase() : null);
                            const isSupported = isForgeSupported(fileType);
                            const isRvtOrDwg = fileName.toUpperCase().endsWith('.RVT') || fileName.toUpperCase().endsWith('.DWG');
                            const isAlreadyTranslated =
                              item.forgeStatus === 'SUCCESS' || item.forgeStatus === 'COMPLETE';
                            const isTranslating =
                              translatingFiles.has(item.fileId || item.id) ||
                              item.forgeStatus === 'IN_PROGRESS' ||
                              item.forgeStatus === 'INPROGRESS';

                            // Hide while translating or already translated — status badge / ⋮ menu covers those
                            return (isSupported || isRvtOrDwg) && !isAlreadyTranslated && !isTranslating;
                          })() && (
                            <Box sx={{ mt: 0.75, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                              {(() => {
                                const fileId = item.fileId || item.id;
                                const isStorageFile = (item.fileId && typeof item.fileId === 'string' && item.fileId.startsWith('storage_')) ||
                                  (!item.fileId && item.id && typeof item.id === 'string' && item.id.startsWith('storage_'));
                                const isInProgressStatus = item.forgeStatus === 'IN_PROGRESS' || item.forgeStatus === 'INPROGRESS';
                                // Only treat progress as "in progress" when status is actually IN_PROGRESS (not FAILED), so user can retry after failure
                                const hasUploadInProgress = isInProgressStatus &&
                                  item.uploadProgress !== null &&
                                  item.uploadProgress !== undefined &&
                                  item.uploadProgress > 0 &&
                                  item.uploadProgress < 100;
                                const hasTranslationInProgress = isInProgressStatus &&
                                  item.translationProgress !== null &&
                                  item.translationProgress !== undefined &&
                                  item.translationProgress > 0 &&
                                  item.translationProgress < 100;
                                const isFailed = item.forgeStatus === 'FAILED';
                                const anotherBusy = isTranslationBusyFor(fileId);
                                const disabledReason = anotherBusy
                                  ? 'Maximum of 3 translations in progress. Wait until one finishes.'
                                  : translatingFiles.has(fileId) ||
                                    isInProgressStatus
                                  ? 'Translating...'
                                  : isStorageFile
                                  ? 'File is only in storage. After Sync Folders, wait a few seconds then refresh the list or re-open the folder so Translate becomes available.'
                                  : hasUploadInProgress || hasTranslationInProgress
                                  ? 'Upload in progress'
                                  : isFailed
                                  ? 'Retry translation'
                                  : 'Translate to Forge';
                                return (
                                  <Tooltip title={disabledReason} placement="top">
                                    <span>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={
                                          translatingFiles.has(fileId) ||
                                          (item.forgeStatus === 'IN_PROGRESS' || item.forgeStatus === 'INPROGRESS') ? (
                                            <CircularProgress size={14} color="inherit" />
                                          ) : (
                                            <CloudUpload fontSize="small" />
                                          )
                                        }
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleTranslate(item);
                                        }}
                                        disabled={
                                          anotherBusy ||
                                          translatingFiles.has(fileId) ||
                                          isInProgressStatus ||
                                          hasUploadInProgress ||
                                          hasTranslationInProgress ||
                                          (item.fileId && typeof item.fileId === 'string' && item.fileId.startsWith('storage_')) ||
                                          (!item.fileId && item.id && typeof item.id === 'string' && item.id.startsWith('storage_'))
                                        }
                                        sx={{
                                          fontSize: '0.7rem',
                                          px: 1,
                                          py: 0.5,
                                          minWidth: 'auto',
                                          ...(isFailed && { color: 'warning.contrastText', bgcolor: 'warning.main' }),
                                          '&:disabled': {
                                            opacity: 0.6,
                                          },
                                        }}
                                      >
                                        {translatingFiles.has(fileId) ||
                                         (item.forgeStatus === 'IN_PROGRESS' || item.forgeStatus === 'INPROGRESS')
                                          ? 'Translating...'
                                          : anotherBusy
                                          ? 'Wait…'
                                          : isFailed
                                          ? 'Retry'
                                          : 'Translate'}
                                      </Button>
                                    </span>
                                  </Tooltip>
                                );
                              })()}
                            </Box>
                          )}
                        </>
                      )}
                      {(item.itemType === 'folder' || item.type === 'folder') && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                          Folder
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </CardActionArea>

                {/* File Actions Menu */}
                {(item.itemType === 'file' || (item.type !== 'folder' && item.itemType !== 'folder')) && (
                  <>
                    <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, item)}
                        sx={{ bgcolor: 'background.paper', boxShadow: 1, color: 'text.primary' }}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl) && selectedFileForMenu?.fileId === item.fileId}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={() => { handleFileClick(item); handleMenuClose(); }}>
                        <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
                        <ListItemText>View</ListItemText>
                      </MenuItem>
                      {/* Translate / Re-translate for Forge-supported files */}
                      {isForgeSupported(item.fileType || item.type) && (() => {
                        const fileId = item.fileId || item.id;
                        const isStorageFile = fileId && typeof fileId === 'string' && fileId.startsWith('storage_');
                        const isTranslating = translatingFiles.has(fileId) ||
                                             (item.forgeStatus === 'IN_PROGRESS' || item.forgeStatus === 'INPROGRESS');
                        const isTranslated = item.forgeStatus === 'SUCCESS' || item.forgeStatus === 'COMPLETE';
                        const anotherBusy = isTranslationBusyFor(fileId);

                        return (
                          <MenuItem
                            onClick={() => {
                              if (isTranslated) {
                                requestRetranslate(item);
                              } else {
                                handleTranslate(item);
                              }
                            }}
                            disabled={isStorageFile || isTranslating || anotherBusy}
                          >
                            <ListItemIcon>
                              {isTranslating ? (
                                <CircularProgress size={16} />
                              ) : isTranslated ? (
                                <Refresh fontSize="small" />
                              ) : (
                                <CloudUpload fontSize="small" />
                              )}
                            </ListItemIcon>
                            <ListItemText>
                              {isStorageFile
                                ? 'Sync to Enable Translation'
                                : isTranslated
                                ? 'Re-translate'
                                : isTranslating
                                ? 'Translating...'
                                : anotherBusy
                                ? 'Wait — 3 translations in progress'
                                : item.forgeStatus === 'FAILED'
                                ? 'Retry translation'
                                : 'Translate to Forge'}
                            </ListItemText>
                          </MenuItem>
                        );
                      })()}
                      {isForgeSupported(item.fileType || item.type) &&
                        (item.forgeStatus === 'SUCCESS' ||
                          item.forgeStatus === 'COMPLETE' ||
                          item.contentOutdated) && (
                        <MenuItem
                          onClick={() => {
                            setHistoryDialog({ open: true, file: item });
                            handleMenuClose();
                          }}
                        >
                          <ListItemIcon>
                            <HistoryIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>History</ListItemText>
                        </MenuItem>
                      )}
                      <MenuItem onClick={() => { onShare && onShare(item); handleMenuClose(); }}>
                        <ListItemIcon><Share fontSize="small" /></ListItemIcon>
                        <ListItemText>Share</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={overageDialog.open} onClose={closeOverageDialog} fullWidth maxWidth="sm">
        <DialogTitle>Extra cost warning</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 0.5 }}>
            This translation exceeds a limit and may incur extra cost. Type{' '}
            <strong>
              {(overageDialog.confirmationWord || 'APPROVE').toLowerCase()}
            </strong>{' '}
            to continue.
          </Alert>
          {(overageDialog.warnings || [])
            // Hide GB/MB size comparisons from the approve dialog (monthly file-count warnings stay)
            .filter((warning) => !/\d+(\.\d+)?\s*(GB|MB|KB)\b/i.test(String(warning || '')))
            .map((warning) => (
            <Typography key={warning} variant="body2" sx={{ mb: 1 }}>
              {String(warning).replace(/\s*soft\s+/gi, ' ')}
            </Typography>
          ))}
          <TextField
            fullWidth
            autoFocus
            margin="dense"
            label={`Type ${(overageDialog.confirmationWord || 'APPROVE').toLowerCase()} to confirm`}
            value={overageDialog.typedValue}
            onChange={(e) =>
              setOverageDialog((prev) => ({ ...prev, typedValue: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOverageDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={confirmOverageTranslate}
            disabled={
              overageDialog.typedValue.trim().toUpperCase() !==
              (overageDialog.confirmationWord || 'APPROVE').toUpperCase()
            }
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={retranslateDialog.open} onClose={closeRetranslateDialog} fullWidth maxWidth="sm">
        <DialogTitle>Re-translate file?</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 0.5 }}>
            This starts a <strong>new Autodesk translation job</strong> for the current synced file
            {retranslateDialog.file
              ? ` (${retranslateDialog.file.fileName || retranslateDialog.file.name || 'file'})`
              : ''}
            . It counts toward translation usage. The 3D viewer will show a processing state until the new translation finishes; then the updated model loads.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRetranslateDialog}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={confirmRetranslate}>
            Re-translate
          </Button>
        </DialogActions>
      </Dialog>

      <TranslationHistoryDialog
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ open: false, file: null })}
        file={historyDialog.file}
      />
    </Box>
  );
};

export default FolderContentView;

